import { type DragEndEvent } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import { type FC, useLayoutEffect, useState } from "react"
import { useNavigate } from "react-router"

import { type ThinBundle } from "@forento/core/models/bundle"

import Button, { SubmitButton } from "~/components/Button"
import InputField from "~/components/InputField"
import Layout, { PageHeader, PageTitle } from "~/components/Layout"
import LoadingError from "~/components/LoadingError"
import {
	DefaultModal,
	ModalButtons,
	ModalHeader,
	ModalHelpCenter,
	ModalHelpCenterIcon,
	ModalInputs,
	ModalTitle,
} from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import SortableListContext from "~/contexts/SortableListContext"
import { usePlatform } from "~/contexts/UserContext"
import withAccessRequirement from "~/hocs/withAccessRequirement"
import usePrice from "~/hooks/usePrice"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import Bundle from "./Bundle"

const BundlesPage = withAccessRequirement(
	"bundles",
	<PageHeader>
		<PageTitle>Bundles</PageTitle>
	</PageHeader>,
	() => {
		const platform = usePlatform()!
		const alert = useAlert()
		const navigate = useNavigate()

		const [isCreateOpen, setCreateOpen] = useState(false)
		const [name, setName] = useState("")
		const [shortDescription, setShortDescription] = useState("")
		const { price, priceValue, setPrice } = usePrice()
		const [isSubmitting, setSubmitting] = useState(false)

		const { data, error, refetch } = query.bundle.list.useQuery()

		async function handleCreate() {
			if (name.trim().length === 0 || priceValue === null) {
				await alert.show("Error", "Please fill in all required fields.")
				return
			}

			setSubmitting(true)
			try {
				const bundle = await trpc.bundle.create.mutate({ name, shortDescription, price: priceValue })
				await refetch()
				setName("")
				setShortDescription("")
				setPrice("")
				setCreateOpen(false)
				navigate(routes.bundle.detail(bundle.id))
			} catch (error) {
				setSubmitting(false)
				console.error(error)
				await alert.show("Error", "Failed to create bundle.")
			}
			setSubmitting(false)
		}

		return (
			<Layout>
				<DefaultModal onClose={() => setCreateOpen(false)} isOpen={isCreateOpen} onSubmit={handleCreate}>
					<ModalHeader>
						<ModalTitle>Create a new bundle</ModalTitle>
						<ModalHelpCenter newTab onClick="https://forento.link/create-bundle">
							<ModalHelpCenterIcon />
							Learn more
						</ModalHelpCenter>
					</ModalHeader>
					<ModalInputs>
						<InputField label="Name *" value={name} onChange={setName} />
						<InputField label="Short description" value={shortDescription} onChange={setShortDescription} />
						<InputField label={`Price (${platform.currency}) *`} value={price} onChange={setPrice} />
					</ModalInputs>
					<ModalButtons>
						<Button variant="secondary" onClick={() => setCreateOpen(false)} isDisabled={isSubmitting}>
							Cancel
						</Button>
						<SubmitButton variant="primary" isLoading={isSubmitting}>
							Create bundle
						</SubmitButton>
					</ModalButtons>
				</DefaultModal>
				<PageHeader>
					<PageTitle>Bundles</PageTitle>
					<Button variant="primary" onClick={() => setCreateOpen(true)}>
						Create new
					</Button>
				</PageHeader>
				{error && data === undefined ? (
					<LoadingError subject="bundles" error={error} onRetry={refetch} />
				) : !data ? (
					<PartialLoadingPage />
				) : (
					<Content bundles={data} reload={refetch} />
				)}
			</Layout>
		)
	},
)

const Content: FC<{ bundles: ThinBundle[]; reload(): Promise<unknown> }> = ({ bundles, reload }) => {
	const [sortOrder, setSortOrder] = useState<number[]>([])

	useLayoutEffect(() => {
		setSortOrder(bundles.map(bundle => bundle.id))
	}, [bundles])

	function handleDragEnd(event: DragEndEvent) {
		const { active, over } = event
		if (!over || active.id === over.id) return

		const newItems = arrayMove(sortOrder, sortOrder.indexOf(Number(active.id)), sortOrder.indexOf(Number(over.id)))
		setSortOrder(newItems)

		for (const bundle of bundles) {
			trpc.bundle.update.mutate({
				id: bundle.id,
				data: { sortOrder: newItems.indexOf(bundle.id) },
			})
		}
	}

	return (
		<SortableListContext items={sortOrder} onDragEnd={handleDragEnd}>
			<div className="flex flex-col gap-2">
				{[...bundles]
					.sort((a, b) => sortOrder.indexOf(a.id) - sortOrder.indexOf(b.id))
					.map(bundle => (
						<Bundle key={bundle.id} bundle={bundle} onDelete={reload} />
					))}
			</div>
		</SortableListContext>
	)
}

export default BundlesPage
